import { useState } from "react";
import { Loader2, MapPin, Sparkles, Plus, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/webadvisor/LeadDetails";

export const CompetitorFinder = ({ auditId, googleEnabled, busy, onScan }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState({});
  const [manual, setManual] = useState("");
  const [finding, setFinding] = useState("");

  const find = async (source) => {
    setFinding(source);
    try {
      const { data } = await api.post(`/api/webadvisor/audit/${auditId}/competitors/suggest`, { query: q, source });
      setQ(data.query);
      setItems(data.suggestions);
      setPicked(Object.fromEntries(data.suggestions.filter(s => s.url).slice(0, 5).map(s => [s.url, true])));
      if (!data.suggestions.length) toast.info("No competitors found — try a different search");
      if (source === "ai") toast.info("AI suggestions — double-check each website before scanning");
    } catch (e) { toast.error(errMsg(e, "Couldn't find competitors")); } finally { setFinding(""); }
  };
  const add = () => {
    const u = manual.trim();
    if (!u) return;
    setItems(x => [{ name: "", url: u, source: "manual" }, ...x]);
    setPicked(p => ({ ...p, [u]: true }));
    setManual("");
  };
  const chosen = items.filter(i => i.url && picked[i.url]);

  return (
    <div className="space-y-3" data-testid="competitor-finder">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="auto repair near Dallas, TX" data-testid="competitor-query-input" />
        <button onClick={() => find("google")} disabled={!!finding || !googleEnabled} title={googleEnabled ? "" : "Add the Google Maps key to turn this on"} className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40 whitespace-nowrap" data-testid="competitor-google-btn">
          {finding === "google" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}Google Maps
        </button>
        <button onClick={() => find("ai")} disabled={!!finding} className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50 whitespace-nowrap" data-testid="competitor-ai-btn">
          {finding === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}AI suggest
        </button>
      </div>
      {!googleEnabled && <p className="text-xs text-slate-400" data-testid="competitor-google-off">Google Maps lookup is off until the Google Maps API key is added.</p>}
      <div className="flex gap-2">
        <Input value={manual} onChange={e => setManual(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="Or type a competitor's website" data-testid="competitor-manual-input" />
        <button onClick={add} className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-3 rounded-lg" data-testid="competitor-manual-add"><Plus className="w-4 h-4" />Add</button>
      </div>
      {items.length > 0 && (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="competitor-suggestions">
          {items.map((s, i) => (
            <label key={i} className={`flex items-center gap-3 px-3 py-2 text-sm ${s.url ? "cursor-pointer" : "opacity-50"}`} data-testid={`competitor-suggestion-${i}`}>
              <Checkbox checked={!!picked[s.url]} disabled={!s.url} onCheckedChange={v => setPicked(p => ({ ...p, [s.url]: !!v }))} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{s.name || s.url}</p>
                <p className="text-xs text-slate-500 truncate">{s.url || "no website listed"}{s.address ? ` · ${s.address}` : ""}</p>
              </div>
              {s.rating && <span className="text-xs text-amber-600 flex items-center gap-0.5 shrink-0"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{s.rating} ({s.reviews || 0})</span>}
              <span className="text-[10px] uppercase font-bold text-slate-400 shrink-0">{s.source}</span>
            </label>
          ))}
        </div>
      )}
      <button onClick={() => onScan(chosen.slice(0, 8))} disabled={busy || !chosen.length} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="scan-competitors-btn">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}Scan {chosen.length || ""} competitor{chosen.length === 1 ? "" : "s"}
      </button>
      {chosen.length > 8 && <p className="text-xs text-amber-700">Only the first 8 are scanned.</p>}
    </div>
  );
};
